import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, ProgressBar, ListGroup, InputGroup, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { fulfillmentAPI, materialsAPI } from '../utils/api';

const OrderPicking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [progress, setProgress] = useState(null);
  const [barcode, setBarcode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalProgress, setTotalProgress] = useState(0);
  const [stockError, setStockError] = useState(null);

  // Add item modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Add item modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSelected, setAddSelected] = useState(null);
  const [addQty, setAddQty] = useState('1');
  const [addUnitCost, setAddUnitCost] = useState('');
  const [addHasVat, setAddHasVat] = useState(false);
  const [addSearching, setAddSearching] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const barcodeInputRef = useRef(null);
  const focusDebounceRef = useRef(null);
  const addSearchDebounceRef = useRef(null);

  useEffect(() => {
    if (orderId) {
      loadProgress();
    }
  }, [orderId]);

  const loadProgress = async () => {
    try {
      setLoading(true);
      const res = await fulfillmentAPI.getProgress(orderId);
      setOrder(res.data.data.order);
      setProgress(res.data.data.progress);
      setTotalProgress(res.data.data.totalProgress);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();

    if (!barcode) {
      setError('กรุณาสแกนบาร์โค้ด');
      return;
    }

    setScanning(true);
    setError('');
    setSuccess('');

    try {
      const res = await fulfillmentAPI.scanItem({
        orderId: parseInt(orderId),
        barcode: barcode.trim(),
        quantity: parseFloat(quantity)
      });

      setSuccess(res.data.message || 'สแกนสินค้าสำเร็จ');

      setBarcode('');
      setQuantity('1');

      await loadProgress();

      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    } catch (err) {
      const errorCode = err.response?.data?.errorCode;
      const details = err.response?.data?.details;

      if (errorCode === 'INSUFFICIENT_STOCK' && details) {
        setStockError({
          materialName: details.materialName,
          availableStock: details.availableStock,
          requestedQuantity: details.requestedQuantity,
          shortage: details.shortage,
          unit: details.unit,
          barcode: details.barcode,
          currentPicked: details.currentPicked,
          totalRequired: details.totalRequired
        });
        setError('');
      } else {
        let errorMessage = err.response?.data?.message || 'เกิดข้อผิดพลาดในการสแกน';

        if (details) {
          if (details.availableStock !== undefined && details.requestedQuantity !== undefined) {
            errorMessage += `\n\nรายละเอียด:\n`;
            errorMessage += `- สต็อกคงเหลือ: ${details.availableStock} ${details.unit || ''}\n`;
            errorMessage += `- จำนวนที่ต้องการ: ${details.requestedQuantity} ${details.unit || ''}\n`;
            errorMessage += `- ขาดอีก: ${details.requestedQuantity - details.availableStock} ${details.unit || ''}`;
          }

          if (details.materialName) {
            errorMessage += `\n\nสินค้า: ${details.materialName}`;
          }

          if (details.currentPicked !== undefined && details.totalRequired !== undefined) {
            errorMessage += `\n\nสถานะการเบิก:\n`;
            errorMessage += `- เบิกแล้ว: ${details.currentPicked} ${details.unit || ''}\n`;
            errorMessage += `- ต้องเบิกทั้งหมด: ${details.totalRequired} ${details.unit || ''}`;
          }
        }

        setError(errorMessage);
        setStockError(null);

        setBarcode('');
        setQuantity('1');

        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
      }
    } finally {
      setScanning(false);
    }
  };

  const handleForceFulfill = async () => {
    if (!stockError) return;

    setScanning(true);
    setError('');

    try {
      const res = await fulfillmentAPI.scanItem({
        orderId: parseInt(orderId),
        barcode: stockError.barcode,
        quantity: stockError.requestedQuantity,
        force: true
      });

      setSuccess(res.data.message || 'บังคับเบิกสินค้าสำเร็จ');
      setStockError(null);

      setBarcode('');
      setQuantity('1');

      await loadProgress();

      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบังคับเบิก');
      setStockError(null);
    } finally {
      setScanning(false);
    }
  };

  const handleCancelForceFulfill = () => {
    setStockError(null);
    setBarcode('');
    setQuantity('1');
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  const handleComplete = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmComplete = async () => {
    setCompleting(true);
    try {
      await fulfillmentAPI.completePicking(orderId);
      setShowConfirmModal(false);
      navigate('/fulfillment');
    } catch (err) {
      let errorMessage = err.response?.data?.message || 'เกิดข้อผิดพลาดในการยืนยัน';

      if (err.response?.data?.details?.stockIssues) {
        const stockIssues = err.response.data.details.stockIssues;
        errorMessage += '\n\nรายการสินค้าที่สต็อกไม่เพียงพอ:\n';
        stockIssues.forEach((issue, index) => {
          errorMessage += `\n${index + 1}. ${issue.materialName} (${issue.materialCode})\n`;
          errorMessage += `   - ต้องการ: ${issue.required} ${issue.unit}\n`;
          errorMessage += `   - มีอยู่: ${issue.available} ${issue.unit}\n`;
          errorMessage += `   - ขาดอีก: ${issue.shortage} ${issue.unit}`;
        });
      }

      setError(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setCompleting(false);
    }
  };

  const handleRemoveItem = async (item) => {
    const pickedQty = parseFloat(item.quantityPicked) || 0;
    const confirmMsg = pickedQty > 0
      ? `ลบรายการ "${item.material.name}"? สต็อกที่เบิกไปแล้ว ${pickedQty} ${item.material.unit} จะถูกคืน`
      : `ลบรายการ "${item.material.name}"?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await fulfillmentAPI.removeItem(item.id, parseInt(orderId));
      setSuccess(`ลบรายการ "${item.material.name}" สำเร็จ`);
      await loadProgress();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบรายการ');
    }
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);

    if (focusDebounceRef.current) {
      clearTimeout(focusDebounceRef.current);
    }

    focusDebounceRef.current = setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 300);
  };

  // Add item modal handlers
  const handleAddSearch = (q) => {
    setAddSearch(q);
    setAddSelected(null);

    if (addSearchDebounceRef.current) {
      clearTimeout(addSearchDebounceRef.current);
    }

    if (!q || q.trim().length < 2) {
      setAddResults([]);
      return;
    }

    addSearchDebounceRef.current = setTimeout(async () => {
      setAddSearching(true);
      try {
        const res = await materialsAPI.search(q.trim());
        setAddResults(res.data.data || res.data || []);
      } catch {
        setAddResults([]);
      } finally {
        setAddSearching(false);
      }
    }, 300);
  };

  const handleAddSelect = (material) => {
    setAddSelected(material);
    setAddSearch(material.name);
    setAddResults([]);
    setAddUnitCost(material.salePrice != null ? String(material.salePrice) : '');
  };

  const handleAddSubmit = async () => {
    if (!addSelected || !addQty || addUnitCost === '') return;

    setAddSubmitting(true);
    try {
      await fulfillmentAPI.addItem({
        orderId: parseInt(orderId),
        materialId: addSelected.id,
        quantity: parseFloat(addQty),
        unitCost: parseFloat(addUnitCost),
        hasVat: addHasVat
      });
      setSuccess(`เพิ่มรายการ "${addSelected.name}" สำเร็จ`);
      setShowAddModal(false);
      setAddSearch('');
      setAddResults([]);
      setAddSelected(null);
      setAddQty('1');
      setAddUnitCost('');
      setAddHasVat(false);
      await loadProgress();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการเพิ่มรายการ');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddSearch('');
    setAddResults([]);
    setAddSelected(null);
    setAddQty('1');
    setAddUnitCost('');
    setAddHasVat(false);
  };

  if (loading) {
    return (
      <Container fluid className="main-content text-center py-5">
        <div className="spinner-border text-primary"></div>
        <p className="mt-2">กำลังโหลด...</p>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container fluid className="main-content">
        <Alert variant="danger">ไม่พบคำสั่งซื้อ</Alert>
      </Container>
    );
  }

  const allComplete = progress && progress.length > 0 && progress.every(p => p.isComplete);

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-box-seam me-2"></i>
            เบิกสินค้า - คำสั่งซื้อ #{order.id}
          </h2>
          <p className="text-muted">
            สาขา: {order.orderedBy.fullName} |
            วันที่สั่ง: {new Date(order.createdAt).toLocaleDateString('th-TH')}
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {stockError && (
        <Card className="mb-4 border-warning">
          <Card.Header className="bg-warning text-dark">
            <h5 className="mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              สต็อกไม่เพียงพอ
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col>
                <p className="mb-2"><strong>สินค้า:</strong> {stockError.materialName}</p>
                <p className="mb-2"><strong>สต็อกคงเหลือ:</strong> {stockError.availableStock} {stockError.unit}</p>
                <p className="mb-2"><strong>จำนวนที่ต้องการ:</strong> {stockError.requestedQuantity} {stockError.unit}</p>
                <p className="mb-3 text-danger">
                  <strong>ขาดอีก:</strong> {stockError.shortage} {stockError.unit}
                </p>
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  หากดำเนินการต่อ สต็อกจะติดลบ <strong>-{stockError.shortage} {stockError.unit}</strong> และต้องเติมสต็อกภายหลัง
                </Alert>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelForceFulfill}
                    disabled={scanning}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleForceFulfill}
                    disabled={scanning}
                  >
                    {scanning ? 'กำลังดำเนินการ...' : 'บังคับเบิก'}
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Row className="mb-4">
        <Col>
          <Card className="border-primary">
            <Card.Body>
              <h5>ความคืบหน้า</h5>
              <ProgressBar
                now={totalProgress}
                label={`${Math.round(totalProgress)}%`}
                variant={allComplete ? 'success' : 'primary'}
                className="mb-2"
                style={{ height: '25px' }}
              />
              <small className="text-muted">
                {progress ? progress.filter(p => p.isComplete).length : 0} / {progress ? progress.length : 0} รายการเสร็จสมบูรณ์
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={5}>
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">สแกนสินค้า</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleScan}>
                <Form.Group className="mb-3">
                  <Form.Label>จำนวน</Form.Label>
                  <InputGroup>
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleQuantityChange(Math.max(0.01, parseFloat(quantity) - 1).toString())}
                      disabled={scanning || parseFloat(quantity) <= 0.01}
                    >
                      <i className="bi bi-dash"></i>
                    </Button>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={scanning}
                      className="text-center"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleQuantityChange((parseFloat(quantity) + 1).toString())}
                      disabled={scanning}
                    >
                      <i className="bi bi-plus"></i>
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>บาร์โค้ด</Form.Label>
                  <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="สแกนบาร์โค้ด..."
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    autoFocus
                    disabled={scanning}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mb-2"
                  disabled={scanning || !barcode}
                >
                  {scanning ? 'กำลังบันทึก...' : 'สแกน'}
                </Button>

                <Button
                  variant={allComplete ? 'success' : 'outline-success'}
                  className="w-100"
                  onClick={handleComplete}
                  disabled={loading || !allComplete}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  ยืนยันการเบิกสินค้า
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">รายการสินค้า</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowAddModal(true)}
              >
                <i className="bi bi-plus-circle me-1"></i>
                เพิ่มรายการ
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush">
                {progress && progress.map((item) => (
                  <ListGroup.Item key={item.id}>
                    <Row className="align-items-center">
                      <Col>
                        <div className="d-flex align-items-center">
                          {item.isComplete ? (
                            <i className="bi bi-check-circle-fill text-success me-2" style={{fontSize: '1.5rem'}}></i>
                          ) : (
                            <i className="bi bi-circle text-muted me-2" style={{fontSize: '1.5rem'}}></i>
                          )}
                          <div>
                            <strong>{item.material.name}</strong><br/>
                            <small className="text-muted">
                              {item.material.code} | Barcode: {item.material.barcode}
                            </small>
                          </div>
                        </div>
                      </Col>
                      <Col xs="auto" className="text-end">
                        <div>
                          <Badge bg={item.isComplete ? 'success' : 'warning'}>
                            {item.quantityPicked} / {item.quantity} {item.material.unit}
                          </Badge>
                        </div>
                        <small className="text-muted">
                          {Math.round(item.progress)}%
                        </small>
                        <div className="mt-1">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveItem(item)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </Col>
                    </Row>
                    {!item.isComplete && (
                      <ProgressBar
                        now={item.progress}
                        className="mt-2"
                        style={{ height: '5px' }}
                      />
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Complete Modal */}
      <Modal show={showConfirmModal} onHide={() => !completing && setShowConfirmModal(false)} centered>
        <Modal.Header closeButton={!completing}>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
            ยืนยันการเบิกสินค้า
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            การดำเนินการนี้จะ<strong>ปิดคำสั่งซื้อ #{order?.id}</strong> และทำเครื่องหมายว่าจัดส่งเสร็จสิ้นแล้ว
          </p>
          <Alert variant="warning" className="mb-0">
            <ul className="mb-0 ps-3">
              <li>ไม่สามารถเพิ่มหรือแก้ไขรายการสินค้าได้อีก</li>
              <li>กดปุ่มนี้เมื่อจัดส่งสินค้าให้สาขาเรียบร้อยแล้วเท่านั้น</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={completing}>
            ยกเลิก
          </Button>
          <Button variant="success" onClick={handleConfirmComplete} disabled={completing}>
            {completing ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>กำลังยืนยัน...</>
            ) : (
              <><i className="bi bi-check-circle me-2"></i>ยืนยัน ส่งสินค้าแล้ว</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Item Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-plus-circle me-2"></i>
            เพิ่มรายการสินค้า
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>ค้นหาสินค้า</Form.Label>
            <Form.Control
              type="text"
              placeholder="ค้นหาชื่อหรือรหัสสินค้า..."
              value={addSearch}
              onChange={(e) => handleAddSearch(e.target.value)}
              autoFocus
            />
            <Form.Text className="text-muted">กรุณากรอกอย่างน้อย 2 ตัวอักษร</Form.Text>
          </Form.Group>

          {addSearching && (
            <div className="text-center py-2">
              <div className="spinner-border spinner-border-sm text-primary me-2"></div>
              <small>กำลังค้นหา...</small>
            </div>
          )}

          {addResults.length > 0 && !addSelected && (
            <ListGroup className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {addResults.map((m) => (
                <ListGroup.Item
                  key={m.id}
                  action
                  onClick={() => handleAddSelect(m)}
                  style={{ cursor: 'pointer' }}
                >
                  <strong>{m.name}</strong>
                  <span className="text-muted ms-2">({m.code})</span>
                  {m.barcode && <small className="text-muted ms-2">Barcode: {m.barcode}</small>}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {addSelected && (
            <>
              <Alert variant="info" className="mb-3">
                <i className="bi bi-check-circle me-2"></i>
                <strong>{addSelected.name}</strong>
                <span className="text-muted ms-2">({addSelected.code})</span>
                <Button
                  variant="link"
                  size="sm"
                  className="ms-2 p-0"
                  onClick={() => { setAddSelected(null); setAddSearch(''); }}
                >
                  เปลี่ยน
                </Button>
              </Alert>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>จำนวน</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={addQty}
                      onChange={(e) => setAddQty(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>ราคา/หน่วย (฿)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={addUnitCost}
                      onChange={(e) => setAddUnitCost(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <Form.Check
                      type="checkbox"
                      label="มี VAT"
                      checked={addHasVat}
                      onChange={(e) => setAddHasVat(e.target.checked)}
                      className="mt-2"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddModal}>
            ยกเลิก
          </Button>
          <Button
            variant="primary"
            onClick={handleAddSubmit}
            disabled={!addSelected || !addQty || addUnitCost === '' || addSubmitting}
          >
            {addSubmitting ? 'กำลังเพิ่ม...' : 'เพิ่มรายการ'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrderPicking;
